import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    if (!this.isSlideTransition) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
    }
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested;

    this.resume();

    this.current = this.dataset.initialSlide ? parseInt(this.dataset.initialSlide, 10) : 0;

    let visibleSlidesAmount = 0;

    if (this.isSlideTransition) {
      this.#initSlideTransition();
      visibleSlidesAmount = 1;
      if (this.autoplay) {
        this.play();
      }
    } else if (this.current !== 0) {
      this.select(this.current, undefined, { animate: false });
      visibleSlidesAmount = 1;
    } else {
      visibleSlidesAmount = this.#updateVisibleSlides();
      if (visibleSlidesAmount === 0) {
        this.select(0, undefined, { animate: false });
        visibleSlidesAmount = 1;
      }
    }

    this.#resizeObserver = new ResizeObserver(async () => {
      if (viewTransition.current) await viewTransition.current;

      if (visibleSlidesAmount > 1) {
        this.#updateVisibleSlides();
      }

      if (this.hasAttribute('auto-hide-controls')) {
        this.#updateControlsVisibility();
      }
    });

    this.#resizeObserver.observe(this.refs.slideshowContainer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { scroller } = this.refs;

    scroller.removeEventListener('mousedown', this.#handleMouseDown);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scroll?.destroy();

    if (this.#transitionTimeout) {
      clearTimeout(this.#transitionTimeout);
      this.#transitionTimeout = null;
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /** Indicates whether the slideshow uses the premium horizontal slide transition. */
  get isSlideTransition() {
    return (
      this.getAttribute('transition-mode') === 'slide' ||
      this.classList.contains('custom_class_hero_slider') ||
      this.closest('.custom_class_hero_slider') !== null
    );
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get defaultSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    // Guard if invalid
    if (requestedIndex === undefined || isNaN(requestedIndex)) return;

    const { slides } = this;

    if (!slides?.length) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { current } = this;
    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    if (this.isSlideTransition) {
      const instant = !animate;
      const userInitiated = event != null;

      if (this.autoplay && !this.paused) {
        this.suspend();
        this.play();
      }

      let direction = 1;
      if (requestedIndex < 0) {
        direction = -1;
      } else if (requestedIndex > lastIndex) {
        direction = 1;
      } else if (requestedIndex < current) {
        direction = -1;
      } else {
        direction = 1;
      }

      this.#performSlideTransition(current, index, direction, instant, userInitiated);
      return;
    }

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      const currentSlide = slides[current];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');
    this.#scroll.to(slide, { instant });
    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if ((!this.isSlideTransition && this.matches(':hover')) || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return this.isSlideTransition ? 5000 : undefined;

    return Math.max(3, value) * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return getVisibleElements(this.refs.scroller, this.slides, SLIDE_VISIBLITY_THRESHOLD, 'x');
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    if (this.isSlideTransition) return;
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging || this.isSlideTransition) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    return this.dataset.initialSlide ? parseInt(this.dataset.initialSlide, 10) : 0;
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Update aria-hidden based on visibility
    slides.forEach((slide) => {
      const isVisible = visibleSlides.includes(slide);
      slide.setAttribute('aria-hidden', `${!isVisible}`);
    });

    return visibleSlides.length;
  }

  #isTransitioning = false;
  #activeTransitionTarget = null;
  #activeTransitionSource = null;
  #transitionTimeout = null;
  #lastUserInitiated = false;

  #initSlideTransition() {
    const slides = this.slides;
    if (!slides || !slides.length) return;

    const initial = this.dataset.initialSlide ? parseInt(this.dataset.initialSlide, 10) : 0;
    const activeIndex = clamp(initial, 0, slides.length - 1);

    slides.forEach((slide, i) => {
      slide.style.transition = 'none';
      if (i === activeIndex) {
        slide.style.transform = 'translate3d(0, 0, 0)';
        slide.style.visibility = 'visible';
        slide.style.zIndex = '1';
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.style.transform = 'translate3d(100%, 0, 0)';
        slide.style.visibility = 'hidden';
        slide.style.zIndex = '1';
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.#current = activeIndex;
    this.#updateSlideControls(activeIndex);
    this.#initSwipeListeners();
  }

  #initSwipeListeners() {
    const scroller = this.refs.scroller;
    if (!scroller || this._hasSwipeListeners) return;
    this._hasSwipeListeners = true;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;
    let isTouching = false;

    scroller.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchEndX = touchStartX;
        touchEndY = touchStartY;
        isTouching = true;
      },
      { passive: true }
    );

    scroller.addEventListener(
      'touchmove',
      (e) => {
        if (!isTouching || e.touches.length !== 1) return;
        touchEndX = e.touches[0].clientX;
        touchEndY = e.touches[0].clientY;
      },
      { passive: true }
    );

    scroller.addEventListener(
      'touchend',
      () => {
        if (!isTouching) return;
        isTouching = false;
        const diffX = touchEndX - touchStartX;
        const diffY = touchEndY - touchStartY;

        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 40) {
          if (diffX < 0) {
            this.next(new CustomEvent('swipe'));
          } else {
            this.previous(new CustomEvent('swipe'));
          }
        }
      },
      { passive: true }
    );
  }

  #updateSlideControls(index) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;
    if (current) current.textContent = `${index + 1}`;
    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === index}`));
    }
    if (previous) previous.disabled = Boolean(!this.infinite && index === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && index >= slides.length - 1);
  }

  #settleSlideTransition() {
    if (!this.#isTransitioning) return;
    if (this.#transitionTimeout) {
      clearTimeout(this.#transitionTimeout);
      this.#transitionTimeout = null;
    }

    const targetIndex = this.#activeTransitionTarget;
    const sourceIndex = this.#activeTransitionSource;
    const slides = this.slides;

    if (slides && targetIndex !== null && targetIndex !== undefined) {
      const targetSlide = slides[targetIndex];
      const sourceSlide = sourceIndex !== null && sourceIndex !== undefined ? slides[sourceIndex] : null;

      if (sourceSlide && sourceSlide !== targetSlide) {
        sourceSlide.style.transition = 'none';
        sourceSlide.style.transform = 'translate3d(100%, 0, 0)';
        sourceSlide.style.visibility = 'hidden';
        sourceSlide.style.zIndex = '1';
        sourceSlide.setAttribute('aria-hidden', 'true');
      }

      if (targetSlide) {
        targetSlide.style.transition = 'none';
        targetSlide.style.transform = 'translate3d(0, 0, 0)';
        targetSlide.style.visibility = 'visible';
        targetSlide.style.zIndex = '1';
        targetSlide.setAttribute('aria-hidden', 'false');
      }

      this.#current = targetIndex;
      this.#updateSlideControls(targetIndex);
    }

    this.#isTransitioning = false;
    this.#activeTransitionTarget = null;
    this.#activeTransitionSource = null;
    this.removeAttribute('transitioning');
  }

  #performSlideTransition(currentIndex, targetIndex, direction, instant, userInitiated) {
    const slides = this.slides;
    if (!slides || !slides.length) return;

    if (this.#isTransitioning) {
      this.#settleSlideTransition();
      currentIndex = this.current;
    }

    if (currentIndex === targetIndex) return;

    const currentSlide = slides[currentIndex];
    const targetSlide = slides[targetIndex];
    if (!targetSlide) return;

    this.#lastUserInitiated = userInitiated;

    if (instant) {
      if (currentSlide && currentSlide !== targetSlide) {
        currentSlide.style.transition = 'none';
        currentSlide.style.transform = 'translate3d(100%, 0, 0)';
        currentSlide.style.visibility = 'hidden';
        currentSlide.style.zIndex = '1';
        currentSlide.setAttribute('aria-hidden', 'true');
      }
      targetSlide.style.transition = 'none';
      targetSlide.style.transform = 'translate3d(0, 0, 0)';
      targetSlide.style.visibility = 'visible';
      targetSlide.style.zIndex = '1';
      targetSlide.setAttribute('aria-hidden', 'false');

      this.current = targetIndex;
      this.#centerSelectedThumbnail(targetIndex, 'instant');

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: targetIndex,
          previousIndex: currentIndex,
          userInitiated,
          trigger: 'select',
          slide: targetSlide,
          id: targetSlide.getAttribute('slide-id'),
        })
      );
      return;
    }

    const dir = direction >= 0 ? 1 : -1;
    this.#isTransitioning = true;
    this.setAttribute('transitioning', '');
    this.#activeTransitionTarget = targetIndex;
    this.#activeTransitionSource = currentIndex;

    // Immediately update dots and pagination
    this.#updateSlideControls(targetIndex);
    this.#centerSelectedThumbnail(targetIndex, 'smooth');

    // Prepare positions without animation
    targetSlide.style.transition = 'none';
    if (currentSlide) currentSlide.style.transition = 'none';

    const targetInitialTransform = dir === 1 ? 'translate3d(100%, 0, 0)' : 'translate3d(-100%, 0, 0)';
    targetSlide.style.transform = targetInitialTransform;
    targetSlide.style.visibility = 'visible';
    targetSlide.style.zIndex = '2';
    targetSlide.setAttribute('aria-hidden', 'false');

    if (currentSlide) {
      currentSlide.style.transform = 'translate3d(0, 0, 0)';
      currentSlide.style.visibility = 'visible';
      currentSlide.style.zIndex = '1';
    }

    // Force style recalculation so browser recognizes starting positions
    void targetSlide.offsetHeight;

    const durationMs = 800;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const transitionVal = `transform ${durationMs}ms ${easing}`;

    // Double rAF: first rAF commits the initial off-screen paint, second rAF
    // starts the animation — guaranteeing a real paint boundary between setup and transition.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.#isTransitioning) return;

        targetSlide.style.transition = transitionVal;
        targetSlide.style.transform = 'translate3d(0, 0, 0)';

        if (currentSlide) {
          const currentTargetTransform = dir === 1 ? 'translate3d(-100%, 0, 0)' : 'translate3d(100%, 0, 0)';
          currentSlide.style.transition = transitionVal;
          currentSlide.style.transform = currentTargetTransform;
        }
      });
    });

    this.#transitionTimeout = setTimeout(() => {
      this.#finalizeSlideTransition(currentIndex, targetIndex);
    }, durationMs + 20);
  }

  #finalizeSlideTransition(fromIndex, toIndex) {
    if (!this.#isTransitioning) return;
    if (this.#transitionTimeout) {
      clearTimeout(this.#transitionTimeout);
      this.#transitionTimeout = null;
    }

    const slides = this.slides;
    if (slides) {
      const prevSlide = slides[fromIndex];
      const activeSlide = slides[toIndex];

      if (prevSlide && prevSlide !== activeSlide) {
        prevSlide.style.transition = 'none';
        prevSlide.style.transform = 'translate3d(100%, 0, 0)';
        prevSlide.style.visibility = 'hidden';
        prevSlide.style.zIndex = '1';
        prevSlide.setAttribute('aria-hidden', 'true');
      }

      if (activeSlide) {
        activeSlide.style.transition = 'none';
        activeSlide.style.transform = 'translate3d(0, 0, 0)';
        activeSlide.style.visibility = 'visible';
        activeSlide.style.zIndex = '1';
        activeSlide.setAttribute('aria-hidden', 'false');
      }
    }

    this.#isTransitioning = false;
    this.#activeTransitionTarget = null;
    this.#activeTransitionSource = null;
    this.removeAttribute('transitioning');

    this.current = toIndex;

    const activeSlide = slides?.[toIndex];
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: toIndex,
        previousIndex: fromIndex,
        userInitiated: this.#lastUserInitiated,
        trigger: 'select',
        slide: activeSlide,
        id: activeSlide?.getAttribute('slide-id'),
      })
    );
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
